// The hub's data layer (R229).
//
// Two things live here, and neither one draws anything:
//
//  1. Pure functions the panels shape a figure with — a countdown, the
//     arcade counter, the seat summary, the badge rules and the rename
//     state machine. They take numbers and give strings, so vitest can hold
//     every state without a DOM.
//  2. `HubData`, the fetch-on-open cache. A panel asks for a cell and gets
//     one of four states back at once; the load happens behind it and the
//     shell redraws when the answer lands.
//
// The rule the whole file follows: **the server ships instants, and the hub
// does the arithmetic** (R220). Nothing here parses a sentence the server
// wrote, and nothing here keeps a formatted countdown: a countdown ticks,
// and a string does not.
//
// Every instant in this file is a unix second on the **wall clock**. The
// world clock and the map clock of numbers.md §19 pace a world, and the
// meta server hosts none.

import { sellsNothing } from '../../platform/platform';
import { runeCount, tidySpaces } from '../../util/text';
import {
  ApiError, CODE_INVALID_NAME, CODE_NAME_TAKEN, CODE_RENAME_SPENT, messageOf,
  KEY_PREMIUM, KEY_SUPPORTER,
  type ArcadeRuns, type ClanWar, type MaintenanceWindow, type Me, type ModeInfo, type NewsItem,
  type Placement,
} from '../../net/api';
import { HUB_QUEUE_WARN_SEC } from '../../gen/presentation';
import { MODE_CLANWAR, NAME_MAX_LEN, NAME_MIN_LEN } from '../../gen/balance';
import { briefing as B, hubState as S, LOCALE } from '../../gen/strings';
import { type BriefRow, seatRows, seatSiegeRow } from '../briefing';
import { groupThousands } from '../../locale/group';
import { isoNear, pad2, type StampOpts, zoneLocale, zoneName } from '../../locale/stamp';
import { readStore, writeStore } from '../localstore';

// --- time ------------------------------------------------------------------

/**
 * How the hub writes an absolute time for one player (R1752): local, and
 * ending in the short name of the zone, so "22:55" never reads as another
 * zone's hour. The name takes the page language, in the region of the
 * browser where it states one (`zoneLocale`).
 */
export const HUB_ZONE: StampOpts = {
  zone: true,
  locale: zoneLocale(LOCALE, typeof navigator === 'undefined' ? [] : navigator.languages ?? []),
};

/**
 * An IANA zone with its short name at `nowSec`, as a row of hours names
 * it (R1752): "Europe/Berlin · CEST". The short name is the mark every
 * hub time ends in, so the reader can match the two. A zone whose short
 * name is the IANA name, or that has none, reads the IANA name alone.
 */
export function zoneLabel(iana: string, nowSec: number): string {
  const mark = zoneName(nowSec, { locale: HUB_ZONE.locale, timeZone: iana });
  return mark === '' || mark === iana ? iana : S.zone_label({ zone: iana, mark });
}

/**
 * A span of wall seconds, as the hub prints it. The largest two units are
 * shown and the rest is dropped, because a countdown is read at a glance:
 * "2D 06H", "06H 12M", "12M 30S". A span that has run out is "NOW".
 */
export function formatSpan(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return S.now;
  const s = Math.floor(sec);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (d > 0) return S.span_dh({ d, h: pad2(h) });
  if (h > 0) return S.span_hm({ h: pad2(h), m: pad2(m) });
  return S.span_ms({ m: pad2(m), s: pad2(rest) });
}

/**
 * The seconds from `now` to `at`, never below zero. An absent instant gives
 * -1, which is the caller's signal that there is nothing to count to.
 */
export function secondsUntil(now: number, at?: number): number {
  if (at === undefined || !Number.isFinite(at)) return -1;
  return Math.max(0, Math.floor(at - now));
}

/** A span that is already past, as the hub prints it: "02M 00S AGO". */
export function formatAgo(now: number, at?: number): string {
  if (at === undefined || !Number.isFinite(at)) return S.never;
  const sec = Math.max(0, Math.floor(now - at));
  if (sec < 60) return S.just_now;
  return S.ago({ span: formatSpan(sec) });
}

/**
 * 1 becomes "1ST", 2 "2ND", 3 "3RD".
 *
 * The four endings are four sentences of the locale, so a language with
 * one ordinal form writes the same text in all four keys and this rule
 * then answers it for every count.
 */
export function ordinal(n: number): string {
  const abs = Math.abs(Math.floor(n));
  const tens = abs % 100;
  if (tens >= 11 && tens <= 13) return S.ordinal_th({ n: abs });
  switch (abs % 10) {
    case 1: return S.ordinal_st({ n: abs });
    case 2: return S.ordinal_nd({ n: abs });
    case 3: return S.ordinal_rd({ n: abs });
    default: return S.ordinal_th({ n: abs });
  }
}

/** A count with a space every three digits, so a ladder lines up. The
 *  mark is the plain space this one column is measured in; every other
 *  figure of the client takes the thin space of `locale/group.ts`. */
export function groupDigits(n: number): string {
  return groupThousands(n, ' ');
}

// --- entitlements ----------------------------------------------------------

/** The three tiers, as the badge names them. */
export type Tier = 'free' | 'supporter' | 'premium';

/** Every entitlement key the account holds right now. */
export function heldKeys(me: Me): Set<string> {
  const out = new Set<string>();
  for (const e of me.entitlements ?? []) if (e.code) out.add(e.code);
  return out;
}

/** True when the account holds one key. It reads the list in place and
 *  builds no set, because a panel asks it on each draw. */
export function hasKey(me: Me, key: string): boolean {
  return (me.entitlements ?? []).some((e) => !!e.code && e.code === key);
}

/**
 * True when the account is in a clan (R232). The clanwar mode demands one,
 * so the tile reads this rather than an entitlement key. A server that
 * predates the `/api/me` field sends nothing, and the answer is then false:
 * the tile says the account is in no clan, and the join route stays the
 * arbiter either way.
 */
export function inClan(me: Me): boolean {
  return (me.clan?.id ?? 0) > 0;
}

/**
 * The banner the hub flies for this account (R242).
 *
 * It is the clan's faction while the account is in a clan, and the
 * account's own faction otherwise. A clan hands its banner to every member
 * it takes in (R240), so the two agree for most accounts; they disagree
 * only for the season in which a player joined a clan of another faction,
 * because a world keeps the faction it seated that player with.
 *
 * The hub follows the clan, and the game follows `Hello.faction` — the
 * faction worn in that world. That split is deliberate: the menu says which
 * banner the player belongs to now, and the map says which one they sail
 * under today.
 *
 * The answer is 0 when no faction is known: a server that predates the
 * field, or an account whose first join has not rolled one yet. `applyTheme`
 * resolves 0 to the Abyssal Compact, which is the look the stylesheet
 * declares, so nothing flashes.
 */
export function hubFaction(me: Me): number {
  const banner = inClan(me) ? me.clan?.faction : undefined;
  if (typeof banner === 'number' && banner > 0) return banner;
  const own = me.account.faction;
  return typeof own === 'number' && own > 0 ? own : 0;
}

/**
 * The tier the badge shows. It is read from the keys and never from a plan
 * name: a grant an operator made by hand is worth the same as a
 * subscription, and the ticket carries keys and not tiers.
 */
export function tierOf(me: Me): Tier {
  const keys = heldKeys(me);
  if (keys.has(KEY_PREMIUM)) return 'premium';
  if (keys.has(KEY_SUPPORTER)) return 'supporter';
  return 'free';
}

// --- the mode tiles --------------------------------------------------------

/** Where a refusal line takes the player. '' is a refusal nothing presses
 *  through to: a closed window and a full mode both wait, and neither one
 *  has a page that fixes it. */
export type ModeFix = '' | 'shop' | 'clan';

/** What one mode tile says, once the schedule, the keys and the clan are
 *  folded in. */
export interface ModeView {
  mode: string;
  /** True when a join would be let through: the window is open, the
   *  account holds what the mode demands, and a world of this mode has a
   *  free seat. */
  joinable: boolean;
  /** The line a joinable tile carries. */
  state: string;
  /** Why a tile is not joinable, without the words of its fix: the tile
   *  writes those after it (R1734). A reader must be able to read the
   *  reason and the countdown (R229). */
  reason: string;
  /** The section the reason line presses through to, or ''. */
  fix: ModeFix;
  /** The tag in the tile's corner. */
  corner: string;
}

/** What the clanwar tile says to an account that is in no clan (R232). Its
 *  fix words press through to the CLAN section, which is where a player
 *  fixes it. */
export const NO_CLAN_REASON = S.no_clan_reason;

/**
 * Folds one `/api/modes` row, the account's keys, its clan and the clock
 * into the three lines a tile draws.
 *
 * `keys` is what the account holds; `member` says whether the account is in
 * a clan, which only the clanwar mode reads (R232). `held` says the account
 * already sits in a seat of this mode (R235). All three come in as plain
 * values, because this function stays pure and a test drives every state
 * without a network.
 *
 * The order of the refusals is the order a player can act on them. What the
 * mode demands is named first: a key the account does not hold, or the clan
 * it is not in. That is the one the player can do something about now. A
 * closed window is named next, with the countdown to the next one. An empty
 * mode is named last.
 *
 * **A held seat answers the empty-mode refusal and no other** (R235). "No
 * world of this mode has a free seat" is about opening a *new* seat, and a
 * player who already holds one opens none: they return to the seat they
 * have. The other three refusals stay, because the server applies them to a
 * resume as well — `modeAllowed` runs on every join, and a reconnect into a
 * held seat is such a call (R232 §7.3). A tile that cleared them would
 * offer a press the server answers with a refusal.
 */
export function modeView(
  m: ModeInfo, now: number, keys: Set<string>, member = false, held = false,
): ModeView {
  const needs = m.entitlement ?? '';
  const entitled = needs === '' || keys.has(needs);
  // The clanwar mode is the one that demands a clan. Every other mode is
  // indifferent to it, and this function must never lock one on it.
  const needsClan = m.mode === MODE_CLANWAR;
  // The corner names what the mode demands: the tier that sells its key,
  // the clan the clanwar mode wants, or what kind of mode it is (R232). A
  // mode with no schedule that is not open reads CLOSED, never OPEN over
  // its own refusal (R2175).
  const corner = needs !== '' ? (m.tier ?? needs).toUpperCase()
    : needsClan ? S.corner_clans
      : m.scheduled ? S.corner_event : m.open ? S.corner_open : S.corner_closed;

  let state = m.current?.closesAt !== undefined
    ? S.state_running_ends({ left: formatSpan(secondsUntil(now, m.current.closesAt)) })
    : m.scheduled ? S.state_running : S.state_always_open;
  if (m.population > 0) {
    state = S.state_with_population({ state, n: groupDigits(m.population) });
  }

  let reason = '';
  let fix: ModeFix = '';
  if (!entitled) {
    // The line names the tier a player can buy and never the key's own
    // code: "event" is not a product (R232). A key no tier sells is an
    // admin grant, so the line names the key and points at no shop.
    const tier = m.tier ?? '';
    reason = tier === ''
      ? S.reason_key_only({ key: needs.toUpperCase() })
      : S.reason_tier_only({ tier: tier.toUpperCase() });
    // A store app sells nothing, so the line points at no shop (R2927).
    fix = tier === '' || sellsNothing() ? '' : 'shop';
  } else if (needsClan && !member) {
    reason = NO_CLAN_REASON;
    fix = 'clan';
  } else if (!m.open) {
    const opensIn = secondsUntil(now, m.next?.opensAt);
    // The line names no window. The owner chose the short form for every
    // scheduled mode: a tile already carries the window's label in its
    // kicker, and the reader wants the span (R232).
    reason = opensIn >= 0
      ? S.reason_starts_in({ left: formatSpan(opensIn) })
      // A mode with no schedule at all is not between two windows: it is a
      // mode this deployment does not run yet, and it must say that rather
      // than name a schedule that does not exist.
      : m.scheduled ? S.reason_no_window : S.reason_not_open_yet;
  } else if (m.worlds <= 0 && !held) {
    reason = S.reason_no_free_seat;
  }

  return { mode: m.mode, joinable: reason === '', state, reason, fix, corner };
}

/**
 * The line the PLAY panel shows over the mode tiles while a maintenance
 * window is planned (deployment.md §6.1).
 *
 * It names the local time of the window and the span that is left, because
 * a person who plans a session needs both: the time says whether the
 * evening is spoilt, and the span says whether there is room for one more
 * season hour.
 *
 * It refuses nothing: the gate is not up yet, and a player who presses a
 * tile now plays until the window opens. `now` is the server's own instant
 * out of `/api/modes`, never `Date.now()` — the R220 rule — so a telephone
 * with a wrong clock still reads the right span.
 *
 * The empty string is the answer where no window is planned, and where the
 * window the server named has already passed.
 */
export function maintenanceLine(w: MaintenanceWindow | undefined, now: number): string {
  if (w === undefined || w.startsAt <= 0 || w.startsAt <= now) return '';
  return S.maintenance_at({
    time: isoNear(w.startsAt, now, HUB_ZONE), left: formatSpan(secondsUntil(now, w.startsAt)),
  });
}

/** One mode row by name, or undefined where the server listed none. */
export function findMode(modes: ModeInfo[], mode: string): ModeInfo | undefined {
  return modes.find((m) => m.mode === mode);
}

/**
 * Whether the PLAY panel draws the seat card of one placement (R1561).
 *
 * A seat card stands only while its seat can be joined now, and the test is
 * the tile's own: the mode row of `/api/modes`, read by `modeView` as a mode
 * the account holds a seat in. The server runs the key, the clan and the
 * window check on every join, a resume included (R235), so a seat whose
 * tile refuses it is a seat no press can reach. An event seat between two
 * runs is that case: the window close drains the event worlds, but the
 * placement stays live until the next run rolls the world.
 *
 * The world status is not part of the test. The join route resumes a held
 * seat in every world that is not retired, and `/api/me` already drops the
 * retired worlds.
 *
 * `modes` is undefined while the schedule has not landed, or when its read
 * failed. The panel then has no evidence against a seat, so the card
 * stands, and the server's own refusal answers a press.
 */
export function seatJoinable(
  p: Placement, modes: ModeInfo[] | undefined, now: number, keys: Set<string>, member: boolean,
): boolean {
  if (modes === undefined) return true;
  const m = findMode(modes, p.mode);
  return m !== undefined && modeView(m, now, keys, member, true).joinable;
}

// --- the Clan War registration ---------------------------------------------

/** The cache key of `GET /api/clan/clanwar`. The CLAN panel and the PLAY
 *  panel read it with one key, so the two make one request (R2826). */
export const CLANWAR_CELL = 'clanwar';

/** The two batch outcomes that leave a registered clan out of the run
 *  (numbers.md §14.4, R1263): no clan of the division to meet, or no
 *  world seat for the pair. */
const NO_PLACE_OUTCOMES: ReadonlySet<string> = new Set(['no_opponent', 'unplaced_no_seat']);

/**
 * True when the answer of `GET /api/clan/clanwar` says that the clan holds
 * no place in its run (R2826): the clan did not register, or the cut-off
 * batch left it out. The join of a member of such a clan meets
 * `no_banner`, so MUSTER of the CLAN WAR tile takes no press.
 *
 * An answer that is not there yet, or a run the calendar does not name,
 * gives no evidence against the clan, and the answer is false. An outcome
 * of '' (the batch has not read the list) or `placed` is false too.
 */
export function clanHoldsNoPlace(war: ClanWar | undefined): boolean {
  if (!war || !war.run) return false;
  const reg = war.registration;
  if (!reg) return true;
  return NO_PLACE_OUTCOMES.has(reg.outcome);
}

// --- the arcade counter ----------------------------------------------------

/** The three states of the daily arcade counter (R221). */
export interface ArcadeState {
  kind: 'unlimited' | 'left' | 'spent';
  /** The tile's corner tag: "UNLIMITED", "2/3", "0/1". */
  corner: string;
  /** The line under the name. */
  line: string;
  /** Seconds to the reset, or -1 where nothing is counted to. */
  resetIn: number;
}

/**
 * Turns the run counter into the three states a tile draws. `unlimited` is
 * a state of its own and never a large number, so the view can never
 * recognise "999" as "no cap".
 */
export function arcadeState(a: ArcadeRuns | undefined, now: number): ArcadeState {
  if (!a) return { kind: 'left', corner: '—', line: S.arcade_not_loaded, resetIn: -1 };
  if (a.unlimited) {
    return {
      kind: 'unlimited',
      corner: S.arcade_unlimited_corner,
      line: S.arcade_unlimited_line,
      resetIn: -1,
    };
  }
  const limit = a.limit ?? 0;
  const left = a.remaining ?? Math.max(0, limit - a.used);
  const resetIn = secondsUntil(now, a.resetsAt);
  if (left <= 0) {
    const when = resetIn >= 0
      ? S.arcade_reset_suffix({ left: formatSpan(resetIn) }) : '';
    return {
      kind: 'spent',
      corner: `0/${limit}`,
      line: S.arcade_spent_line({ limit, when }),
      resetIn,
    };
  }
  return {
    kind: 'left',
    corner: `${left}/${limit}`,
    line: S.arcade_left_line({ left, limit }),
    resetIn,
  };
}

// --- the seat card ---------------------------------------------------------

/** One seat, as the PLAY panel reads it. Every field is a finished string,
 *  because the panel lays them out and computes none of them. */
export interface SeatView {
  world: string;
  season: string;
  /** False when the world has not described the seat (R223). */
  present: boolean;
  rank: string;
  /** The hexes this commander took that still fly the colours. */
  territory: string;
  queue: string;
  /** The colour class of the QUEUE row: good, gold (warn) or bad. */
  queueTone: 'good' | 'gold' | 'bad';
  away: string;
  asOf: string;
  /** True when the seat was attacked while the player was out. */
  raided: boolean;
  /** The rows of the return briefing (R1605), empty when the beat
   *  carried none. A siege the watch states stands in `siege` and not
   *  here. */
  brief: BriefRow[];
  /** The siege against the faction that heads the card (R1743), or null
   *  for none. */
  siege: BriefRow | null;
  /** The object whose window comes next, in capitals (R1752), or ''
   *  when the beat named no window. NEXT WINDOW names it first. */
  windowSite: string;
  /** The NEXT WINDOW time and span, or '' when the beat named no window. */
  window: string;
  /** True while that window is open. */
  windowOpen: boolean;
  /** The SEASON ENDS value, or '' when no beat stated the end. */
  seasonEnd: string;
}

/** The SEASON ENDS value (R1743): the local date and time of the end, and
 *  the span left. '' when the server stated no end. */
function seasonEndLine(endAt: number | undefined, now: number): string {
  if (endAt === undefined || !(endAt > 0)) return '';
  return S.season_ends({ time: isoNear(endAt, now, HUB_ZONE), left: formatSpan(endAt - now) });
}

/**
 * The NEXT WINDOW value (R1743). `inSec` and `shutSec` are the wall seconds
 * from the beat, and `ago` is the age of the beat. A window that has
 * opened reads OPEN NOW and the time to its shut; a window ahead reads the
 * local time it opens and the span left.
 */
function windowLine(
  inSec: number, shutSec: number, ago: number, now: number,
): { text: string; open: boolean } {
  const left = inSec - ago;
  if (left <= 0) return { text: S.window_open_now({ left: formatSpan(shutSec - ago) }), open: true };
  return { text: S.window_at({ time: isoNear(now + left, now, HUB_ZONE), left: formatSpan(left) }), open: false };
}

/** The wall seconds since the seat beat. The seat card and the tile line
 *  count every span of the watch from now with this one age. */
function beatAge(asOf: number, now: number): number {
  return Math.max(0, now - asOf);
}

/** A name of the map in the capitals the hub writes names in. */
function upper(name: string): string {
  return name.toLocaleUpperCase(LOCALE);
}

/** What the tile of a held seat says about the seat (R1752). */
export interface SeatAlert {
  kind: 'siege' | 'window';
  text: string;
}

/**
 * The line the tile of a held seat states in place of YOUR SEAT (R1752),
 * or null where the seat is calm. A siege against the seat's faction comes
 * first, in the Bad ink. Else a window that opens within `leadSec`, or is
 * open now, states its time. The line reads the watch of the beat with the
 * age the seat card reads it with, so the tile and the card agree.
 */
export function seatAlert(p: Placement, now: number, leadSec: number): SeatAlert | null {
  const s = p.seat;
  const w = s?.watch;
  if (!s || !w) return null;
  const ago = beatAge(s.asOf, now);
  const g = (w.sieges ?? [])[0];
  if (g !== undefined) {
    const left = g.windowInSec - ago;
    const site = upper(g.siteName);
    return {
      kind: 'siege',
      text: left < 0 ? S.tile_siege_open({ site }) : S.tile_siege_in({ site, left: formatSpan(left) }),
    };
  }
  if (w.windowSite === '') return null;
  const left = w.windowInSec - ago;
  if (left <= 0) {
    return { kind: 'window', text: S.tile_window_open({ left: formatSpan(w.windowShutSec - ago) }) };
  }
  if (left > leadSec) return null;
  return {
    kind: 'window',
    text: S.tile_window_at({ time: isoNear(now + left, now, HUB_ZONE), left: formatSpan(left) }),
  };
}

/**
 * Shapes one placement. A placement with no `seat` is the honest absent
 * state: the world has not beaten for a minute, or its newest beat belongs
 * to an earlier season. The card then says so and shows no figure but the
 * season end, which the world row states and not the beat.
 */
export function seatView(p: Placement, now: number): SeatView {
  const base: SeatView = {
    world: p.world,
    season: S.season({ n: p.season }),
    present: false,
    rank: '—',
    territory: '—',
    queue: '—',
    queueTone: 'bad',
    away: '—',
    asOf: S.no_word_yet,
    raided: false,
    brief: [],
    siege: null,
    windowSite: '',
    window: '',
    windowOpen: false,
    seasonEnd: seasonEndLine(p.seasonEndAt, now),
  };
  const s = p.seat;
  if (!s) return base;
  // The work the queue still holds is its ceiling less its free room.
  const workSec = Math.max(s.queueMaxSec - s.queueFreeSec, 0);
  const queue = s.queueBusy
    ? S.queue_left({ busy: S.queue_busy({ n: s.queueLen }), h: (Math.round(workSec / 360) / 10).toFixed(1) })
    : S.queue_idle({ left: formatSpan(s.queueFreeSec) });
  const queueTone = !s.queueBusy ? 'bad' : workSec <= HUB_QUEUE_WARN_SEC ? 'gold' : 'good';
  const unread = s.unread <= 0
    ? S.away_nothing
    : `${S.away_unread({ n: s.unread })}${s.unreadBattle > 0 ? S.away_raids({ n: s.unreadBattle }) : ''}`;
  // The return briefing (R1605, card 05): the rows, and the span of the
  // absence beside the unread count. The countdowns count from now, so the
  // age of the beat comes off each of them.
  const b = s.briefing;
  const ago = beatAge(s.asOf, now);
  let brief = b ? seatRows(b, formatSpan, ago) : [];
  const away = b && brief.length > 0
    ? B.away_span({ away: unread, span: formatSpan(b.awaySec + ago) })
    : unread;
  // The watch (R1743): a siege heads the card, so the briefing does not
  // state it a second time.
  const w = s.watch;
  const siege = w ? seatSiegeRow(w.sieges ?? [], formatSpan, ago) : null;
  if (siege !== null) brief = brief.filter((r) => r.key !== 'siege');
  const win = w && w.windowSite !== '' ? windowLine(w.windowInSec, w.windowShutSec, ago, now) : null;
  return {
    ...base,
    present: true,
    rank: S.rank_of({ rank: ordinal(s.rank), total: s.of }),
    territory: S.cells_taken({ n: s.cellsOwned }),
    queue,
    queueTone,
    away,
    asOf: S.as_of({ ago: formatAgo(now, s.asOf) }),
    raided: s.unreadBattle > 0,
    brief,
    siege,
    windowSite: win === null ? '' : upper(w!.windowSite),
    window: win?.text ?? '',
    windowOpen: win?.open ?? false,
  };
}

// --- the badge dots --------------------------------------------------------

/** Where the browser remembers the newest article it has shown. */
export const NEWS_SEEN_KEY = 'aquaphobia.hub.newsSeenAt';

/** The instant one article is dated by. A draft carries no publication
 *  instant, and the public route serves no draft, so `updatedAt` is the
 *  fallback and not the rule. */
export function newsAt(n: NewsItem): number {
  return n.publishedAt ?? n.updatedAt;
}

/** The newest publication instant in a news page, or 0 for an empty one. */
export function newestNewsAt(items: NewsItem[]): number {
  let out = 0;
  for (const n of items) {
    const at = newsAt(n);
    if (Number.isFinite(at) && at > out) out = at;
  }
  return out;
}

/**
 * The NEWS dot. It is on while the newest article is newer than the newest
 * one this browser has opened the panel on. Opening the panel writes the
 * stamp, and the dot goes.
 */
export function newsDot(items: NewsItem[], seenAt: number): boolean {
  return newestNewsAt(items) > seenAt;
}

/** The CLAN dot. It is the live unread count of the chat, so it clears the
 *  moment the panel marks the transcript read. A replayed backlog line is
 *  not news and never counts (`clanchat.ts`). */
export function clanDot(unread: number): boolean {
  return unread > 0;
}

/** The INBOX items the account's seats hold unread: the sum of the seat
 *  cards' counts. The live tab icon marks a hidden hub with it (R1653). A
 *  placement whose world has not beaten for a minute carries no seat and
 *  counts nothing. */
export function seatUnread(me: Pick<Me, 'placements'>): number {
  let n = 0;
  for (const p of me.placements) n += Math.max(0, p.seat?.unread ?? 0);
  return n;
}

/**
 * Reads one "seen at" stamp. A store that refuses gives 0, so the dot
 * shows: a dot a reader has already cleared is a small cost, and a dot
 * nobody ever sees is news lost.
 *
 * The stamp is a PER-VIEWER convenience and lives in this browser alone.
 * Nothing on a server reads it, and a second machine starts with none.
 */
export function loadSeenAt(key: string): number {
  const raw = readStore(key);
  const n = raw === null ? 0 : Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Writes one stamp; a store that refuses just forgets. */
export function saveSeenAt(key: string, at: number): void {
  writeStore(key, String(Math.floor(at)));
}

// --- the rename machine ----------------------------------------------------

// The player name rule (R226) rides data/product/meta.toml
// [meta.social] name_min_len and name_max_len, which the meta server's
// `auth.ValidName` enforces. The refusal line below states it, and reads
// the generated pair rather than spelling it out in the sentence (R351).

/** Why a rename was refused. '' is "nothing was refused". */
export type RenameError = '' | 'name_taken' | 'invalid_name' | 'rename_spent' | 'other';

/**
 * The one-time rename, as a state a view renders and a test drives.
 *
 * The form has two steps (R249). The box is the first one, and the
 * confirmation is the second. A rename is spent the moment the server takes
 * it — a second call answers 409 `rename_spent` — so the player reads the
 * picked name once more, in the form the server will store it, before the
 * call goes out. `confirm` says which step is up, and `pending` holds the
 * name that step names.
 */
export interface RenameState {
  /** True while the inline form is up. */
  open: boolean;
  /** True while the confirmation step is up, in place of the box. */
  confirm: boolean;
  /** True while the call is in flight. */
  busy: boolean;
  /** What the box holds. This is the raw text the player typed, and it
   *  stays as it is while the confirmation is up, so a cancel gives the box
   *  back unchanged. */
  value: string;
  /** The name the call will send, which is the name the server stores. It
   *  is `tidySpaces(value)`, and it is set when the confirmation
   *  opens. It is '' while no confirmation is up. */
  pending: string;
  error: RenameError;
  /** The message beside the error, which is the server's own where there
   *  is one: a client must never parse it, and a person may read it. */
  message: string;
  /** True after the change landed. The pencil never comes back. */
  done: boolean;
}

export function renameIdle(): RenameState {
  return {
    open: false, confirm: false, busy: false, value: '', pending: '',
    error: '', message: '', done: false,
  };
}

/** Opens the form with the current name in the box. */
export function renameOpen(s: RenameState, name: string): RenameState {
  return { ...s, open: true, confirm: false, value: name, pending: '', error: '', message: '' };
}

export function renameClose(s: RenameState): RenameState {
  return { ...renameBack(s), open: false, busy: false };
}

export function renameTyped(s: RenameState, value: string): RenameState {
  return { ...s, value };
}

/**
 * Asks for the confirmation. It carries the box to the second step with the
 * name the server will store.
 *
 * A value the box rules refuse does not move: the state stays on the box
 * and says why. That check is the same hint `renameLooksValid` gives, and
 * the server stays the arbiter of the name it takes.
 */
export function renameAsk(s: RenameState): RenameState {
  if (s.busy) return s;
  // The name as the server will store it. The confirmation names this
  // value and the call sends it, so the player reads the name they get
  // and never the raw box (R249).
  const want = tidySpaces(s.value);
  if (!renameLooksValid(want)) {
    return { ...s, confirm: false, pending: '', error: 'invalid_name', message: '' };
  }
  return { ...s, confirm: true, pending: want, error: '', message: '' };
}

/** Leaves the confirmation for the box. The typed value is still there. */
export function renameBack(s: RenameState): RenameState {
  return { ...s, confirm: false, pending: '', error: '', message: '' };
}

export function renameStart(s: RenameState): RenameState {
  return { ...s, busy: true, error: '', message: '' };
}

/** The refusal codes this module knows, by the wire word. The table is a
 *  `Map`, so a code that names a prototype member reads as `other` (R1341). */
const RENAME_CODES: ReadonlyMap<string, RenameError> = new Map([
  [CODE_NAME_TAKEN, 'name_taken'],
  [CODE_INVALID_NAME, 'invalid_name'],
  [CODE_RENAME_SPENT, 'rename_spent'],
]);

/** The refusal, typed. An error this module does not know becomes `other`,
 *  and the server's own message goes beside it. */
export function renameFailed(s: RenameState, err: unknown): RenameState {
  const code = err instanceof ApiError ? err.code : '';
  // A refusal gives the box back. The player has to change something before
  // the next call, and the box is where they change it.
  return {
    ...renameBack(s),
    busy: false,
    error: RENAME_CODES.get(code) ?? 'other',
    message: messageOf(err),
  };
}

/** The change landed. The form closes and never opens again. */
export function renameDone(s: RenameState): RenameState {
  return { ...renameClose(s), done: true };
}

/** What the view says about each refusal. The two figures of the invalid
 *  name are the meta server's own name rule, so the sentence reads them
 *  from the rule and never spells them out. */
const RENAME_MESSAGES: Record<Exclude<RenameError, ''>, string> = {
  name_taken: S.rename_name_taken,
  invalid_name: S.rename_invalid_name({ name_min: NAME_MIN_LEN, name_max: NAME_MAX_LEN }),
  rename_spent: S.rename_spent,
  other: S.rename_other,
};

export function renameMessageFor(e: RenameError): string {
  return e === '' ? '' : RENAME_MESSAGES[e];
}

/**
 * Whether the box holds something worth sending. This is a **hint** and not
 * the arbiter: `auth.ValidName` on the meta server decides, and a name this
 * function passes can still come back `invalid_name` (R226 §2). It exists
 * so a plainly empty box does not fire a request.
 */
export function renameLooksValid(value: string): boolean {
  const runes = runeCount(tidySpaces(value));
  return runes >= NAME_MIN_LEN && runes <= NAME_MAX_LEN;
}

// --- the fetch-on-open cache ----------------------------------------------

/** What a panel knows about one read. */
export type CellState = 'loading' | 'ready' | 'failed';

/** One cached read. A panel switches on `state` and renders all three. */
export interface Cell<T> {
  state: CellState;
  value?: T;
  error?: unknown;
}

interface Entry {
  /** When the value landed, in wall milliseconds. 0 while nothing has. */
  at: number;
  value?: unknown;
  error?: unknown;
  loading: boolean;
}

/** The default life of a cell that is read while a panel is open. */
export const TTL_LIVE = 30_000;
/** The life of a cell that is read one time when a panel opens. */
export const TTL_ON_OPEN = 5 * 60_000;

/**
 * The hub's cache. A panel calls `cell` inside its render, which is a
 * synchronous function, so the answer is the state and never a promise. A
 * cell that is missing or stale starts its load, and `onChange` fires when
 * the answer lands, which is what makes the shell draw again.
 *
 * One load runs at a time for one key: a panel that renders four times a
 * second must not open four requests.
 */
export class HubData {
  private entries = new Map<string, Entry>();

  constructor(
    private onChange: () => void,
    private clock: () => number = () => Date.now(),
  ) {}

  /**
   * Reads one cell, and starts the load where it is missing or stale.
   *
   * A stale cell keeps its old value while the new one travels, so a panel
   * that refreshes every 30 s never flashes an empty state at the reader.
   */
  cell<T>(key: string, ttlMs: number, load: () => Promise<T>): Cell<T> {
    const now = this.clock();
    let e = this.entries.get(key);
    if (!e) {
      e = { at: 0, loading: false };
      this.entries.set(key, e);
    }
    const stale = e.at === 0 || now - e.at >= ttlMs;
    if (stale && !e.loading) {
      e.loading = true;
      const entry = e;
      const landed = (): void => {
        entry.at = this.clock();
        entry.loading = false;
        this.onChange();
      };
      // A refusal keeps the old value, so a stale cell that fails to
      // refresh still answers with the picture the reader has.
      load().then(
        (value) => { entry.value = value; entry.error = undefined; landed(); },
        (error: unknown) => { entry.error = error; landed(); },
      );
    }
    return this.read(e);
  }

  /** Reads a cell without starting a load. */
  peek<T>(key: string): Cell<T> {
    const e = this.entries.get(key);
    return e ? this.read(e) : { state: 'loading' };
  }

  /** The state one entry is in. A failure that keeps an old value is
   *  `ready`: the reader keeps the picture it has. */
  private read<T>(e: Entry): Cell<T> {
    if (e.error !== undefined && e.value === undefined) return { state: 'failed', error: e.error };
    if (e.value !== undefined) return { state: 'ready', value: e.value as T };
    return { state: 'loading' };
  }

  /** Throws one cell away, so the next read fetches. */
  drop(key: string): void {
    this.entries.delete(key);
  }

  /** Throws away every cell whose key starts with `prefix`. */
  dropPrefix(prefix: string): void {
    // A `Map` keeps iterating over the keys a loop deletes behind it.
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) this.entries.delete(key);
    }
  }

  /** Marks one cell stale without losing its value, so the next read
   *  refreshes behind the picture the reader already has. */
  expire(key: string): void {
    const e = this.entries.get(key);
    if (e) e.at = 0;
  }
}
